import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Server, Globe2, ShieldCheck, Zap, Cpu, Network, Database,
  Code2, Sparkles, Activity, Lock, Cable
} from 'lucide-react';
import PageHero from '../components/PageHero';
import CtaBanner from '../components/CtaBanner';
import FaqAccordion from '../components/FaqAccordion';
import Seo from '../components/Seo';

/**
 * /platform — Infrastructure & trust page.
 *
 * Sells the layer underneath every VoIPinger service: the UC platform,
 * three Tier-4 datacentres, n-share geo-redundancy and the Open API
 * surface. Aimed at IT / procurement / security buyers who need to
 * validate the platform before signing.
 *
 * Content sourced from the platform feature brochure (n-Share Multi-Site
 * Geographic Redundancy, Resilience, Open Platform APIs sections) and the
 * VoIPinger feature description deck.
 */
const PLATFORM_FAQ = [
  {
    q: 'Where is the platform hosted?',
    a: 'Three Equinix Tier-4 datacentres — London (the same campus as the London Stock Exchange), Amsterdam and Las Vegas. They run in an active n+ configuration with 10 Gb resilient interconnects, so every node can pass call traffic simultaneously.'
  },
  {
    q: 'What does "99.99% uptime" actually mean?',
    a: 'It is the contractual SLA across the entire UC platform — not a single datacentre. If one node loses connectivity, registrations and call traffic fail over to the remaining nodes with no downtime, no dropped calls and no loss of configuration data or recordings.'
  },
  {
    q: 'Is the platform Microsoft Teams Direct Routing certified?',
    a: 'Yes. The platform ships with Microsoft-certified Ribbon SBCs as part of TeamMate (our Teams integration). Direct Routing tenants can be provisioned without buying separate SBC hardware.'
  },
  {
    q: 'What API access do we get?',
    a: 'A complete REST API surface — 240+ endpoints and over 300 pre-built orchestrations across provisioning, recordings, conferencing, dial-plan management, contact-centre and real-time control. Anything we expose in the admin portal you can drive from your own systems.'
  },
  {
    q: 'How is voice quality measured?',
    a: 'Every call carries an MOS (Mean Opinion Score) on both legs, with jitter and packet-loss tracking. The Call Trace tool stores the full SIP ladder and PCAP for every call so we can diagnose faults after the fact instead of trying to reproduce them.'
  },
  {
    q: 'What compliance accreditations apply?',
    a: 'The platform supports GDPR-aware data handling, ISO 27001 alignment, and PCI-DSS compatible workflows (pause/resume on recordings, DTMF masking). Specific accreditation status is shared under NDA during the procurement process.'
  }
];

export default function Platform() {
  return (
    <>
      <Seo
        title="Platform & Network | 3 Tier-4 datacentres, 99.99% uptime | VoIPinger"
        description="The VoIPinger UC platform: three Tier-4 datacentres (London Equinix / Amsterdam / Las Vegas), n-Share active n+ geo-redundancy, 240+ open APIs, GDPR + PCI-DSS aligned."
        path="/platform"
        image="https://voipinger.com/assets/heroes/platform.png"
        service={{ name: 'VoIPinger Platform & Network', serviceType: 'Cloud UC platform / carrier-grade telephony' }}
        faq={PLATFORM_FAQ}
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Platform & Network', path: '/platform' }]}
      />
      <PageHero
        eyebrow="Platform & Network"
        title="Carrier-grade platform. Hosted in three Tier-4 datacentres."
        subtitle="The infrastructure your phone system actually runs on — geographic redundancy, carrier-grade resilience and an open API surface. The platform behind every VoIPinger service."
        breadcrumb="Platform"
      />

      {/* ============================================================== */}
      {/* HEADLINE STATS                                                   */}
      {/* ============================================================== */}
      <section className="bg-white">
        <div className="container-x section-pad">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {[
              { v: '99.99%', l: 'Platform uptime SLA', sub: 'Across all three datacentres' },
              { v: '3', l: 'Tier-4 datacentres', sub: 'London / Amsterdam / Las Vegas' },
              { v: '145+', l: 'Countries served', sub: 'Local numbers + global routing' },
              { v: '240+', l: 'Open REST APIs', sub: '300+ orchestrations available' }
            ].map((s) => (
              <div key={s.l} className="rounded-2xl bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-100 p-5 text-center">
                <p className="text-3xl md:text-4xl font-extrabold gradient-text">{s.v}</p>
                <p className="text-xs font-semibold text-slate-800 mt-2">{s.l}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">{s.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/* THREE DATACENTRES                                                */}
      {/* ============================================================== */}
      <section className="relative overflow-hidden bg-gradient-to-b from-violet-50/30 via-white to-white border-y border-slate-100">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow"><Globe2 size={11} /> Global presence</span>
            <h2 className="section-title mt-4">Three datacentres, acting as one platform</h2>
            <p className="section-sub mt-3">
              Each cluster is strategically placed for low-latency regional coverage. n-Share replication
              keeps every node in sync — calls, registrations, recordings and config all flow across the
              fabric in real time.
            </p>
            <div className="divider-svg mt-6" />
          </div>

          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {[
              {
                city: 'London',
                facility: 'Equinix LD-series · Tier-4',
                badge: 'Primary UK',
                bullets: [
                  'Same Equinix campus as the London Stock Exchange',
                  '10 Gb resilient interconnects',
                  'Primary node for UK enterprise traffic'
                ]
              },
              {
                city: 'Amsterdam',
                facility: 'Equinix AM-series · Tier-4',
                badge: 'EMEA',
                bullets: [
                  'Low-latency failover from London',
                  'Active node for European tenants',
                  '10 Gb interconnect to LON cluster'
                ]
              },
              {
                city: 'Las Vegas',
                facility: 'Tier-4 colocation',
                badge: 'Americas',
                bullets: [
                  'Third active node for follow-the-sun ops',
                  'Effectively 100% platform availability',
                  'Independent power + carriers'
                ]
              }
            ].map((d) => (
              <div key={d.city} className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-extrabold uppercase tracking-widest text-violet-600">{d.badge}</p>
                  <Server size={16} className="text-slate-400" />
                </div>
                <h3 className="text-2xl font-extrabold text-slate-900">{d.city}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{d.facility}</p>
                <ul className="mt-4 space-y-2">
                  {d.bullets.map((b) => (
                    <li key={b} className="text-sm text-slate-600 flex items-start gap-2">
                      <span className="text-violet-500 mt-1">•</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/* N-SHARE GEO REDUNDANCY                                          */}
      {/* ============================================================== */}
      <section className="bg-white">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow"><Network size={11} /> n-Share architecture</span>
            <h2 className="section-title mt-4">Geo-redundancy that's actually active-active</h2>
            <p className="section-sub mt-3">
              n-Share is the replication layer that turns three geographically diverse
              clusters into one logical platform. All nodes are online; all nodes can process traffic;
              all nodes share state.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center mb-4 shadow-md shadow-violet-500/20">
                <Cpu size={22} />
              </div>
              <h3 className="font-extrabold text-slate-900 text-lg mb-2">Active n+ configuration</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Every node processes registrations, call traffic, voicemail and configuration in
                parallel. There is no "primary / secondary" — every node is live. If one fails, the
                others absorb the load with no service interruption.
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center mb-4 shadow-md shadow-violet-500/20">
                <Database size={22} />
              </div>
              <h3 className="font-extrabold text-slate-900 text-lg mb-2">Replicated state</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Recordings, voicemail, dial-plans and user provisioning replicate across the cluster.
                Failover doesn't lose context — the same call can be answered on a different node mid-conversation.
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center mb-4 shadow-md shadow-violet-500/20">
                <Activity size={22} />
              </div>
              <h3 className="font-extrabold text-slate-900 text-lg mb-2">Real-time monitoring (NQS / QoS)</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Every call has an MOS quality score, jitter and packet-loss measurement.
                A 24/7 NOC alerts on threshold breaches before customers notice.
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center mb-4 shadow-md shadow-violet-500/20">
                <Zap size={22} />
              </div>
              <h3 className="font-extrabold text-slate-900 text-lg mb-2">Seamless failover</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Node failure is transparent to end users. Registrations re-attach within seconds, call
                traffic re-routes via GeoDNS, and devices stay connected throughout. No reboots, no
                logins, no manual intervention.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/* OPEN API SURFACE                                                 */}
      {/* ============================================================== */}
      <section className="bg-slate-50 border-y border-slate-100">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow"><Code2 size={11} /> Open Platform API</span>
            <h2 className="section-title mt-4">Anything we do, you can automate</h2>
            <p className="section-sub mt-3">
              The platform exposes everything its own admin portal does — and a lot more. Build custom
              dashboards, push provisioning from your CRM, stream CDR into your data warehouse, or
              ship your own white-labelled portal on top.
            </p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-3 max-w-5xl mx-auto">
            {[
              { icon: Server, label: 'Provisioning' },
              { icon: Database, label: 'Recordings' },
              { icon: Network, label: 'Conferencing' },
              { icon: Cable, label: 'Endpoints' },
              { icon: Activity, label: 'Reporting' },
              { icon: ShieldCheck, label: 'System mgmt' },
              { icon: Zap, label: 'Real-time control' },
              { icon: Code2, label: 'Dial-plan mgmt' },
              { icon: Sparkles, label: 'Contact Center' },
              { icon: Lock, label: 'User mgmt' }
            ].map((c) => (
              <div key={c.label} className="rounded-2xl bg-white border border-slate-200 p-4 text-center hover:border-violet-300 hover:shadow-md transition-all">
                <div className="w-10 h-10 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center mx-auto mb-2">
                  <c.icon size={18} />
                </div>
                <p className="text-xs font-bold text-slate-800">{c.label}</p>
              </div>
            ))}
          </div>

          <div className="max-w-3xl mx-auto mt-10 text-center">
            <p className="text-sm text-slate-600 mb-4">
              <strong className="text-slate-900">240+ REST endpoints</strong> · <strong className="text-slate-900">300+ pre-built orchestrations</strong> · webhooks · WebSocket real-time events
            </p>
            <Link to="/contact" className="btn-primary">Request API access <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/* SECURITY & COMPLIANCE                                            */}
      {/* ============================================================== */}
      <section className="bg-white">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow"><Lock size={11} /> Security & compliance</span>
            <h2 className="section-title mt-4">Procurement-ready</h2>
            <p className="section-sub mt-3">
              The platform is built for regulated industries — finance, healthcare, retail, BPO. Here is
              the short version of what your security team is going to ask.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {[
              { i: ShieldCheck, t: 'TLS + SRTP encryption', d: 'All signalling over TLS, all media over SRTP. No clear-text RTP on the wire.' },
              { i: Lock, t: 'PCI-DSS pause / resume', d: 'Mask card-payment portions of recordings with DTMF masking and audit logs.' },
              { i: Database, t: 'Recording retention policy', d: 'Per-tenant retention rules with cryptographic erasure at end of life.' },
              { i: Activity, t: 'Audit trails everywhere', d: 'Every supervisor action — listen, whisper, barge — is logged with timestamps.' },
              { i: Globe2, t: 'GDPR / UK GDPR aligned', d: 'Data residency on UK + EU clusters, DPAs available, SCCs for any cross-border flow.' },
              { i: Network, t: 'Anti-fraud + LCR', d: 'Carrier-grade SBCs with anti-fraud rules; least-cost routing per destination.' }
            ].map((c) => (
              <div key={c.t} className="rounded-2xl bg-white border border-slate-200 p-5 hover:border-violet-300 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center mb-3">
                  <c.i size={18} />
                </div>
                <h3 className="font-bold text-slate-900 mb-1">{c.t}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FaqAccordion items={PLATFORM_FAQ} title="Platform & Infrastructure FAQ" />
      <CtaBanner
        eyebrow="Procurement-ready"
        title="Want the platform spec sheet?"
        subtitle="Architecture diagrams, DR runbook, security questionnaire — share with your IT and procurement teams in one PDF."
      />
    </>
  );
}
